import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import LoadingLogo from '../../components/LoadingLogo';
import GenericCard from '../Users/GenericCard';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';
import DeletePopup from '../../components/DeletePopup';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const PaymentTypes = () => {
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [filters, setFilters] = useState({ needle: '' });
  const [showModule, setShowModule] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [{ name: 'needle', type: 'search' }];

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/payments/types', showSnackbar, { signal });
      const list = res?.data || [];
      setTypes(list);
      setFilteredTypes(list);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching payment types:', err);
    } finally {
      setLoading(false);
    }
  }, [signal]);

  useEffect(() => {
    const ac = new AbortController();
    fetchTypes();
    return () => ac.abort();
  }, [fetchTypes]);

  const applyFilters = useCallback(() => {
    let filtered = [...types];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((pt) => (pt.name || '').toLowerCase().includes(q));
    }

    setFilteredTypes(filtered);
  }, [types, filters]);

  useEffect(() => {
    applyFilters();
  }, [filters, types, applyFilters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '' });
    setFilteredTypes(types);
  };

  const handleAddClick = () => {
    setSelectedType(null);
    setShowModule(true);
  };

  const handleEditClick = (item) => {
    setSelectedType(item);
    setShowModule(true);
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchTypes();
    setShowModule(false);
    setSelectedType(null);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/payments/types/delete/${itemToDelete.id}`, showSnackbar);
      await fetchTypes();
      showSnackbar(t('confirmation.paymentTypeDelete') || 'Type de paiement supprimé', 'success');
    } catch (e) {
      console.error('Error deleting payment type:', e);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const getMenuOptions = (item) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(item) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(item) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.paymentTypes')}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.paymentTypes')}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_paymentType')}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="relative mt-6">
        {loading && (
          <div className="absolute top-0 left-0 w-full flex justify-center z-10">
            <LoadingLogo />
          </div>
        )}

        {/* Cards (no pagination) */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && filteredTypes.map((pt) => (
            <GenericCard
              key={pt.id}
              title={pt.name || ''}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(pt)} />
                </div>
              }
            />
          ))}
        </div>

        {!loading && filteredTypes.length === 0 && (
          <div className="text-center mt-4">
            <p>{t('placeholder.noResults') || 'Aucun résultat.'}</p>
          </div>
        )}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="paymentTypes"
          mode={selectedType ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedType || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default PaymentTypes;
